import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { FileText, Clock, Mail, Building, User, X, Package, Settings2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'

// Helper to format configuration keys
function formatKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

// Helper to format configuration values
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return value.toString()
  if (typeof value === 'string') {
    // Handle yes/no strings
    if (value.toLowerCase() === 'yes') return 'Yes'
    if (value.toLowerCase() === 'no') return 'No'
    return value
  }
  return String(value)
}

// Render configuration as a nice list
function ConfigurationDisplay({ config }: { config: Record<string, unknown> }) {
  const renderConfig = (obj: Record<string, unknown>, depth = 0): React.ReactNode[] => {
    const items: React.ReactNode[] = []

    for (const [key, value] of Object.entries(obj)) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        // Nested object - render its contents with a header
        items.push(
          <div key={key} className={depth > 0 ? 'ml-3' : ''}>
            {depth === 0 && Object.keys(obj).length > 1 && (
              <p className="text-xs font-medium text-secondary-500 mt-2 mb-1 flex items-center gap-1">
                <Settings2 className="w-3 h-3" />
                {formatKey(key)}
              </p>
            )}
            {renderConfig(value as Record<string, unknown>, depth + 1)}
          </div>
        )
      } else if (Array.isArray(value)) {
        items.push(
          <div key={key} className="flex justify-between py-1 border-b border-secondary-100 last:border-0">
            <span className="text-xs text-secondary-500">{formatKey(key)}</span>
            <span className="text-xs font-medium text-secondary-800">{value.join(', ')}</span>
          </div>
        )
      } else {
        items.push(
          <div key={key} className="flex justify-between py-1 border-b border-secondary-100 last:border-0">
            <span className="text-xs text-secondary-500">{formatKey(key)}</span>
            <span className="text-xs font-medium text-secondary-800">{formatValue(value)}</span>
          </div>
        )
      }
    }

    return items
  }

  return (
    <div className="bg-white rounded p-2 space-y-0.5">
      {renderConfig(config)}
    </div>
  )
}

interface QuoteRequestItem {
  id: string
  product_id: string
  configuration: Record<string, unknown> | null
  quantity: number
  products: {
    name: string
    code: string
  } | null
}

interface QuoteRequest {
  id: string
  company_name: string
  contact_name: string
  email: string
  phone: string | null
  message: string | null
  status: 'pending' | 'contacted' | 'completed'
  created_at: string
  quote_request_items?: QuoteRequestItem[]
}

export function QuotesPage() {
  const [selectedQuote, setSelectedQuote] = useState<QuoteRequest | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data: quotes, isLoading } = useQuery({
    queryKey: ['admin-quotes', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('quote_requests')
        .select(`
          *,
          quote_request_items (
            id,
            product_id,
            configuration,
            quantity,
            products (
              name,
              code
            )
          )
        `)
        .order('created_at', { ascending: false })

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter)
      }

      const { data, error } = await query
      if (error) throw error
      return data as QuoteRequest[]
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('quote_requests')
        .update({ status })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-quotes'] })
    },
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'contacted':
        return 'bg-blue-100 text-blue-700'
      case 'completed':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-secondary-100 text-secondary-700'
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Quote Requests</h1>
        <p className="text-secondary-600">Manage customer quote requests</p>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'pending', 'contacted', 'completed'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize',
              statusFilter === status
                ? 'bg-primary-600 text-white'
                : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
            )}
          >
            {status}
          </button>
        ))}
      </div>

      <div className="card">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
          </div>
        ) : quotes && quotes.length > 0 ? (
          <div className="divide-y divide-secondary-100">
            {quotes.map((quote) => (
              <div
                key={quote.id}
                className="p-4 hover:bg-secondary-50 cursor-pointer"
                onClick={() => setSelectedQuote(quote)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building className="w-4 h-4 text-secondary-400" />
                      <span className="font-semibold text-secondary-900">{quote.company_name}</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-secondary-500">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {quote.contact_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {quote.email}
                      </span>
                      {quote.quote_request_items && quote.quote_request_items.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Package className="w-4 h-4" />
                          {quote.quote_request_items.length} item{quote.quote_request_items.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={cn('badge', getStatusColor(quote.status))}>
                      {quote.status}
                    </span>
                    <p className="text-xs text-secondary-400 mt-1 flex items-center justify-end gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(quote.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No quote requests found</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedQuote && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedQuote(null)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ opacity: 0, x: '100%' }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: '100%' }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl z-50 overflow-y-auto"
            >
              <div className="p-6 border-b sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold">Quote Request Details</h2>
                  <button onClick={() => setSelectedQuote(null)} className="p-2 hover:bg-secondary-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-secondary-500">Company</label>
                    <p className="font-medium text-secondary-900">{selectedQuote.company_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-secondary-500">Contact</label>
                    <p className="font-medium text-secondary-900">{selectedQuote.contact_name}</p>
                  </div>
                  <div>
                    <label className="text-sm text-secondary-500">Email</label>
                    <a href={`mailto:${selectedQuote.email}`} className="font-medium text-primary-600 block">
                      {selectedQuote.email}
                    </a>
                  </div>
                  {selectedQuote.phone && (
                    <div>
                      <label className="text-sm text-secondary-500">Phone</label>
                      <a href={`tel:${selectedQuote.phone}`} className="font-medium text-primary-600 block">
                        {selectedQuote.phone}
                      </a>
                    </div>
                  )}
                  {selectedQuote.message && (
                    <div>
                      <label className="text-sm text-secondary-500">Message</label>
                      <p className="text-secondary-700 mt-1">{selectedQuote.message}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-secondary-500">Received</label>
                    <p className="text-secondary-700">
                      {new Date(selectedQuote.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Requested Products */}
                {selectedQuote.quote_request_items && selectedQuote.quote_request_items.length > 0 && (
                  <div className="border-t pt-6">
                    <label className="text-sm font-medium text-secondary-700 mb-3 block">
                      Requested Products ({selectedQuote.quote_request_items.length})
                    </label>
                    <div className="space-y-3">
                      {selectedQuote.quote_request_items.map((item) => (
                        <div key={item.id} className="bg-secondary-50 rounded-lg p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-secondary-900">
                                {item.products?.name || 'Unknown Product'}
                              </p>
                              <p className="text-sm text-secondary-500">
                                Code: {item.products?.code || 'N/A'}
                              </p>
                            </div>
                            <span className="text-sm font-medium text-secondary-700 bg-white px-2 py-1 rounded">
                              Qty: {item.quantity}
                            </span>
                          </div>
                          {item.configuration && Object.keys(item.configuration).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-secondary-200">
                              <p className="text-xs text-secondary-500 mb-1">Configuration:</p>
                              <ConfigurationDisplay config={item.configuration as Record<string, unknown>} />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-6">
                  <label className="text-sm font-medium text-secondary-700 mb-2 block">Update Status</label>
                  <div className="flex gap-2">
                    {['pending', 'contacted', 'completed'].map((status) => (
                      <button
                        key={status}
                        onClick={() => {
                          updateStatusMutation.mutate({ id: selectedQuote.id, status })
                          setSelectedQuote({ ...selectedQuote, status: status as QuoteRequest['status'] })
                        }}
                        className={cn(
                          'px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize',
                          selectedQuote.status === status
                            ? getStatusColor(status)
                            : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                        )}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
